import Grid from "@mui/material/Grid";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

import useStores from "../../../hooks/useStores";
import useGoogleTagManager from "../../../hooks/useGTM";
import { setQueryParams } from "../../../utils";
import {
  ProductGrid,
  RegistryBox,
  RegistryText,
  StyledBox,
  Subtitle,
} from "../styles/ProductStyles";
import { ProductFeatureSection } from "./ProductFeatureSection";
import { ProductHeader } from "./ProductHeader";
import { ProductIconList } from "./ProductIconList";
import { ProductPricePanel } from "./ProductPricePanel";
import { ProductProcedureList } from "./ProductProcedureList";
import { useProductDetails } from "../hooks/useProductDetails";
import { CorrectorData } from "../types/productTypes";

const getContractUrl = (): string => {
  return import.meta.env.VITE_ODONTO_SITE ?? import.meta.env.VITE_PME_URL ?? "";
};

const removeEmptyParams = (
  params: Record<string, string | number | null | undefined>
) => {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      return value !== undefined && value !== null && value !== "";
    })
  );
};

const Product = observer(() => {
  const { pathname } = useLocation();
  const { productStore, correctorAndProducerStore } = useStores();
  const { pageInfoEvent, interactionEvent } = useGoogleTagManager();

  const handleProductStoreChange = useCallback(
    (id: number, fullPrice: number | null, title: string) => {
      productStore.setProduct(
        id,
        productStore.productPlan,
        productStore.productCounter,
        fullPrice,
        title
      );
    },
    [productStore]
  );

  const {
    product,
    productsMenu,
    pageLocation,
    anchorEl,
    isMenuOpen,
    openMenu,
    closeMenu,
    selectProduct,
  } = useProductDetails(productStore.productId, handleProductStoreChange);

  useEffect(() => {
    pageInfoEvent({
      page_location: pageLocation,
      page_path: pathname,
    });
  }, [pageInfoEvent, pageLocation, pathname]);

  const handleEditPlanClick = () => {
    interactionEvent({
      event_action: "clicou",
      event_category: "pagina_plano",
      event_label: "editar_plano",
      event_name: "editar_plano",
    });
  };

  const navigateOut = () => {
    const contractUrl = getContractUrl();

    const correctorData =
      (correctorAndProducerStore.correctorData as CorrectorData | null) ?? {};

    const params = removeEmptyParams({
      ev: correctorData.ev,
      ea: correctorData.ea,
      aa: correctorData.aa,
    });

    const url =
      Object.keys(params).length > 0
        ? setQueryParams(contractUrl, params)
        : contractUrl;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <StyledBox>
      <ProductGrid
        container
        sx={{ flexDirection: { xs: "column-reverse", md: "row" } }}
      >
        <Grid size={{ xs: 12 }}>
          <Grid container>
            <ProductHeader
              product={product}
              productsMenu={productsMenu}
              anchorEl={anchorEl}
              isMenuOpen={isMenuOpen}
              onOpenMenu={openMenu}
              onCloseMenu={closeMenu}
              onSelectProduct={selectProduct}
              onEditPlanClick={handleEditPlanClick}
            />

            <ProductPricePanel product={product} onContract={navigateOut} />
          </Grid>

          <ProductIconList icons={product.icons} />

          <Subtitle>
            Ampla cobertura com {product.procedures.count} procedimentos.
            Conheça os principais:
          </Subtitle>

          <ProductProcedureList procedures={product.procedures} />

          <ProductFeatureSection
            title="Características deste plano"
            groups={product.moreBenefits}
          />

          <ProductFeatureSection
            title="Vantagens deste plano"
            color="#FF5000"
            groups={product.moreAdvantages}
          />
        </Grid>
      </ProductGrid>

      <RegistryBox>
        <RegistryText>{product.registry}</RegistryText>
        <RegistryText>Nome Técnico ANS: {product.registryDesc}</RegistryText>
      </RegistryBox>
    </StyledBox>
  );
});

export default Product;